import { createFileRoute } from '@tanstack/react-router';
import {
  getDestinationByOutputIds,
  loadVideoComposition,
  pointOutputsToVideoCompositionIdByIds,
  getOutputsUsingComposition,
} from './-videoCompositionCrudUtils';
import { Box, Button, Text } from '@streamelements/frontend-ui';
import { DestinationsStorageApiClient } from '../../../services/DestinationsApiClient';
import { useEffect } from 'react';
import { ensureOBSPresence } from '../../../utils/ensureOBSPresence';
import { promisefy } from '../../../utils/promisefy';
import { Analytics } from '../../../services/AnalyticsService';

function useHideVideoCompositionViewOverlay() {
  useEffect(() => {
    let unmounted = false;

    (async () => {
      await ensureOBSPresence();

      if (unmounted) return;
      if (!window.host?.removeVideoCompositionViewOverlay) {
        const { obsVersionString, hostApiVersionString, hostPluginVersionString } = await promisefy(
          window.host.getHostProperties
        )();
        console.warn('Host does not support removeVideoCompositionViewOverlay', {
          obsVersionString,
          hostApiVersionString,
          hostPluginVersionString,
        });
        return;
      }

      window.host?.removeVideoCompositionViewOverlay(() => void 0);
    })();

    return () => {
      unmounted = true;
    };
  }, []);
}

export const Route = createFileRoute('/_authenticated/canvases/$canvasId/')({
  component: Index,
  pendingComponent: Index,
  errorComponent: NotFound,
  notFoundComponent: NotFound,
  loader: async ({ params: { canvasId }, context: { authContextProvider, serviceEnvironment } }) => {
    const destinationsStorage = new DestinationsStorageApiClient({
      authContextProvider,
      serviceEnvironment,
    });

    await destinationsStorage.initDestinations();

    const composition = await loadVideoComposition({
      videoCompositionId: canvasId,
    });

    await destinationsStorage.notifyOBSForActiveStreamingOutputs({ videoCompositionId: canvasId });

    const outputsUsingComposition = await getOutputsUsingComposition(composition);

    const isVertical = composition.videoFrame.width < composition.videoFrame.height;
    const isHorizontal = composition.videoFrame.width > composition.videoFrame.height;

    Analytics.reportMultiStreamingEvent({
      name: 'custom_video_composition_dock_loaded',
      fields: [
        ['video_composition_resolution', `${composition.videoFrame.width}x${composition.videoFrame.height}`],
        ['video_composition_orientation', isVertical ? 'vertical' : isHorizontal ? 'horizontal' : 'square'],
        ['video_composition_scenes_count', composition.scenes.length],
        [
          'video_composition_total_sources_count',
          composition.scenes.reduce((acc, scene) => acc + scene.sources.length, 0),
        ],
        ['outputs_using_composition', outputsUsingComposition.length],
      ],
    });

    return { composition };
  },
});

function Index() {
  useHideVideoCompositionViewOverlay();

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Text.Heading level="3">Loading canvas...</Text.Heading>
      <Text.Body>Awaiting 👍 from OBS.</Text.Body>
    </Box>
  );
}

export function NotFound(params: unknown) {
  console.info('Not found', params);

  const { canvasId } = Route.useParams();
  const { authContextProvider, serviceEnvironment } = Route.useRouteContext();

  useHideVideoCompositionViewOverlay();

  async function closePanel() {
    await ensureOBSPresence();
    const container = await promisefy(window.host.getCurrentContainerProperties)();

    const outputs = await promisefy(window.host.getAllStreamingOutputs)();
    const outputsUsingComposition = Object.values(outputs).filter((output) => output.videoCompositionId === canvasId);

    const destinations = await getDestinationByOutputIds({
      outputIds: outputsUsingComposition.map((output) => output.id),
      authContextProvider,
      serviceEnvironment,
    });

    await pointOutputsToVideoCompositionIdByIds({
      outputIds: outputsUsingComposition.map((output) => output.id),
      targetCanvasId: 'default',
      destinations: Array.from(destinations.values()),
    });

    for (const [id, destination] of destinations) {
      void id;
      await destination.registerDestination();
    }

    return promisefy(window.host.removeDockingWidgetsByIds)([container.id]);
  }

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
      }}
    >
      <Text.Heading level="3">Canvas not found</Text.Heading>
      <Text.Body css={{ px: '1rem', textAlign: 'center', mb: '2rem' }}>
        The canvas you are trying to access does not exist.
      </Text.Body>
      <Text.Body
        css={{
          px: '1rem',
          textAlign: 'center',
          mb: '2rem',
          color: '$textAttention',
        }}
      >
        Closing the panel will make the outputs that were using this canvas to use the default canvas instead.
      </Text.Body>

      <Button
        color="neutral"
        variant="outlined"
        density="compact"
        onClick={() => window.location.reload()}
        css={{ mb: '2rem' }}
      >
        Try again
      </Button>

      <Button color="neutral" variant="ghost" density="compact" onClick={closePanel}>
        Close panel
      </Button>
    </Box>
  );
}
